import { enqueueSnackbar } from "notistack"
import { IDocHubFilters, IProjectMaterialDetailsResponse } from "pages/Projects/ProjectServices"
import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import {
    HistoryResponse,
    IDocHubDocument,
    IFile,
    IResponse,
    StatusButton,
    TicketDataResponseType,
} from "../Common/commonTypes"
import { getTicketDetails as getDemolitionTicketDetails } from "../Operations/features/Demolition/DemolitionSlice"
import { IFinanceOverviewResponse, PaymentTermsResponse, PurchaseOrderDetailsResponse } from "./FinanceTypes"

const MODULE_URL = `finance`

export interface IFinanceTabItem {
    status: string
    status_color: string
    status_id: number[]
    status_key: string
    status_text_color: string
    ticket_count: number
    total_pages: number
}
interface GetSkipStatusesParams {
    search_key?: string
    ticket_id: string
}

interface IParsedFields {
    amount_without_gst: number
    gst_amount: number
    gst_no: string
    credit_note_no: string
    credit_note_date: string
    invoice_date: string
    invoice_no: string
    irn_no: string
    purchase_no: string
    shipping_charges: number
    subtotal: number
    total_amount: number
    revenue_tagging_note: string
}
interface IExtractTaxInvoiceRes {
    low_confidence_flags: {
        amount_without_gst: boolean
        shipping_charges: boolean
    }
    message: string
    parsed_fields: IParsedFields
    s3_url: string
}

type FinanceDocHubFilters = Omit<IDocHubFilters, "project_id"> & {
    ticket_id: number
}

export interface IFinanceSentOnDocItem {
    description: string | null
    type: string | null
    quantity: number | string
    rate: number | null
    amount: number | null
    gst: number | null
    sku_code: string | null
    item_id?: number
    children?: IFinanceSentOnDocItem[]
}
export interface IFinanceInvoiceListItem {
    invoice_id: number | null
    invoice_number: string | null
    po_number: string | null
    type: "invoice" | "vendor_invoice"
    amount: number | null
    gst: number | null
    status: string | null
    items: IFinanceSentOnDocItem[]
}
export interface IFinanceDcListItem {
    dc_number: string | null
    to_number: null
    type: "dc" | "vendor_dc"
    amount: number | null
    gst: number | null
    status: string | null
    items: IFinanceSentOnDocItem[]
}

export interface ITransferOrderProcessSteps {
    document_raised: boolean
    initiated: boolean
    marked_transferred: boolean
    pushed_to_zoho: boolean
    recorded: boolean
    stock_deducted: boolean
    stock_received: boolean
}
export interface ITransferOrderProcess {
    status: "pending" | "in_progress" | "complete"
    next_action: "push_to_zoho" | "initiate" | "mark_transferred" | null
    can_retry: boolean
    steps: ITransferOrderProcessSteps
}
export interface ITransferOrderWarehouseRef {
    id: number
    location: string
    state: string
    warehouse: string
}
export interface ITransferOrderLineItemView {
    item_id: number
    log_item_id: number
    description: string | null
    sku_code: string | null
    type: string | null
    quantity: number
    rate: number | null
    amount: number | null
    gst: number | null
    tax_percentage: number | null
    serial_number: string | null
    destination_inventory_id: number | null
}
export interface ITransferOrderListItem {
    transfer_order_id: number
    to_number: string | null
    zoho_transfer_order_id: string | null
    document_type: "dc" | "invoice" | null
    document_type_label: string | null
    document_id: number | null
    document_number: string | null
    document_date: string | null
    document_zoho_id: string | null
    description: string | null
    is_interstate: boolean
    status: "draft" | "in_transit" | "transferred"
    transfer_date: string | null
    item_count: number
    amount: number | null
    gst: number | null
    from_warehouse: ITransferOrderWarehouseRef | null
    to_warehouse: ITransferOrderWarehouseRef | null
    items: ITransferOrderLineItemView[]
    process: ITransferOrderProcess
}

export interface ICreateTransferOrderLineItem {
    log_item_id: number
    quantity_transfer: number
}
export interface ICreateTransferOrderPayload {
    from_warehouse_id: number
    to_warehouse_id: number
    line_items: ICreateTransferOrderLineItem[]
    transfer_order_number?: string
    description?: string
    ticket_id?: string
    module_id?: number
    submodule_id?: number | null
    user_id?: number
}
export interface ICreateTransferOrderResponse {
    transfer_order_id: number
    transfer_order_number: string | null
    is_interstate: boolean
    status: "draft"
    line_count: number
    pushed: boolean
}
export interface ITransferOrderActionPayload {
    transfer_order_id: number
    ticket_id?: string
    module_id?: number
    submodule_id?: number | null
    user_id?: number
}

// Zoho customer (contact) + address sync — /v3/finance/zoho/customer/*
export interface IZohoCustomerAuditKeys {
    ticket_id?: number
    module_id?: number
    submodule_id?: number | null
}
export interface ICreateZohoCustomerPayload extends IZohoCustomerAuditKeys {
    account_id: number
    billing_address_id?: number
}
export interface ICreateZohoCustomerResponse {
    account_id: number
    zoho_customer_id: string
    contact_name: string
    created: boolean
    matched_on: "column" | "gstin" | "log" | "already_exists" | null
    // The account's other billing GSTINs, synced to Zoho as tax-info records — best-effort, never fails create.
    tax_info?:
        | {
              synced: true
              added: string[]
              skipped: string[]
              failed: string[]
              linked: { address_id: number; gstin: string; tax_info_id: string }[]
          }
        | { synced: false; reason: string }
}
export interface IZohoCustomerAddressPayload extends IZohoCustomerAuditKeys {
    address_id: number
}
export interface IAddZohoCustomerAddressResponse {
    address_id: number
    zoho_address_id: string
    added: boolean
}
export interface IMarkZohoAddressPrimaryResponse {
    address_id: number
    zoho_address_id: string
    marked: "billing" | "shipping"
}

export interface IFinanceItemRow {
    log_item_id: number
    plp_id: number | null
    product_id: number
    item_description: string | null
    type: string | null
    quantity: number
    rate: number | null
    discount: number | null
    tax_percent: number | null
    amount: number | null
    cin: string | null
    status: 0 | 1
    po_items: string | null
    sku_code: string | null
}
export interface IFinanceItemWarehouse {
    id: number
    gst: string | null
    name: string
    state_id: number
    state_name: string
}
export interface IFinanceItemsResponse {
    group_by: string | null
    count: number
    items: IFinanceItemRow[]
    warehouse?: IFinanceItemWarehouse | null
}

export interface IFinanceItemGroupedRow {
    product_id: number
    item_description: string | null
    type: string | null
    quantity: number
    rate: number | null
    discount: number | null
    tax_percent: number | null
    amount: number | null
    cins: string[]
    line_count: number
    billed_count: number
    status: 0 | 1
    sku_code: string | null
}
export interface IFinanceItemsGroupedResponse {
    group_by: string | null
    count: number
    items: IFinanceItemGroupedRow[]
}

export interface ICreateSentOnInvoiceLineItem {
    quantity: number
    rate?: number
    discount?: number
    tax_percentage?: number
    log_item_id?: number
    po_item_id?: number
    po_items?: string | null
}
export interface ICreateSentOnInvoicePayload {
    user_id: number
    invoice_type: "invoice" | "vendor_invoice"
    line_items: ICreateSentOnInvoiceLineItem[]
    sent_on_number?: string
    sent_on_date?: string
    amount?: number
    gst?: number
    discount?: number
    reference_number?: string
    remarks?: string
    po_id?: number
    document_id?: number
    customer_id?: number
}
export interface ICreateSentOnInvoiceResponse {
    invoice_id: number
    message: string
}

export interface IItemParentMapping {
    item_id: number
    parent_id: number | null
}
export interface ILinkItemParentsPayload {
    user_id: number
    invoice_id: number
    mappings: IItemParentMapping[]
}
export interface ILinkItemParentsResponse {
    sent_on_details_id: number
    mapped: number
}

export interface IZohoInvoicePushPayload {
    invoice_id: number
    ticket_id?: number
    module_id?: number
    submodule_id?: number
}
export type IZohoInvoicePushAdjustment =
    | {
          created: boolean
          linked_from_log: boolean
          zoho_adjustment_id: string
          inventory_adjustment_id: number
          line_count: number
      }
    | { skipped: true; reason: string }
    | { error: string }
export interface IZohoInvoicePushResponse {
    sent_on_details_id: number
    zoho_invoice_id: string
    invoice_number: string
    status: string
    created: boolean
    linked_from_log: boolean
    adjustment: IZohoInvoicePushAdjustment
}

export interface IZohoInvoiceStatusPayload {
    invoice_id: number
    user_id?: number
    ticket_id?: number
    module_id?: number
    submodule_id?: number
}
export type IZohoInvoiceStockResult =
    | { deducted: true; lines: number; reservations_released: number }
    | { deducted: false; reason: string }
export interface IZohoInvoiceStatusResponse {
    sent_on_details_id: number
    status: string
    previous_status: string | null
    zoho_invoice_id: string
    invoice_moved: boolean
    adjustment_moved: boolean
    inventory_adjustment_id: number | null
    zoho_adjustment_id?: string
    invoice_stock: IZohoInvoiceStockResult
    adjustment_stock: IZohoInvoiceStockResult
}

const handleSnackbar = async (
    queryFulfilled: any,
    successMsg: (data: any) => string,
    fallbackSuccess = "File Download Successfully",
    fallbackError = "Something went wrong"
) => {
    try {
        const { data } = await queryFulfilled
        enqueueSnackbar(successMsg(data) || fallbackSuccess, { variant: "success" })
    } catch (error: any) {
        enqueueSnackbar(error?.error?.data?.message || error?.error?.data?.error || error?.message || fallbackError, {
            variant: "error",
        })
    }
}

const financeService = api.injectEndpoints({
    endpoints: build => ({
        getFinanceTabs: build.query<IFinanceTabItem[], { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/total`,
                params: {
                    search_key: params.data.search_key ?? "",
                    child_status_ids: JSON.stringify(params.data.child_status_ids ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    country_ids: JSON.stringify(params.data.country_ids ?? []),
                    zone_ids: JSON.stringify(params.data.zone_ids ?? []),
                    region_ids: JSON.stringify(params.data.region_ids ?? []),
                    state_ids: JSON.stringify(params.data.state_ids ?? []),
                    city_ids: JSON.stringify(params.data.city_ids ?? []),
                    site_ids: JSON.stringify(params.data.site_ids ?? []),
                    raised_on_from: params.data.raised_on_from ?? "",
                    raised_on_to: params.data.raised_on_to ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    payment_status: JSON.stringify(params.data.payment_status ?? []),
                    due_date_start: params.data.due_date_start ?? "",
                    due_date_end: params.data.due_date_end ?? "",
                    tag_ids: JSON.stringify(params.data.tag_ids ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    on_hold: params.data.on_hold ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    on_hold_date: params.data.on_hold_date ?? "",
                    product_ids: JSON.stringify(params.data.product_ids ?? []),
                    per_page: params?.selectedValue,
                    page: params?.page,
                    account_id: JSON.stringify(params.data.account_id ?? []),
                    parent_account_id: JSON.stringify(params.data.parent_account_id ?? []),
                    ecom_order_id: params.data.ecom_order_id ? JSON.stringify([params.data.ecom_order_id]) : JSON.stringify([]),
                },
                useV3: true,
            }),
            transformResponse: (response: any): IFinanceTabItem[] => response?.data?.message ?? [],
            providesTags: ["Finance"],
        }),
        getFinanceData: build.query<TicketDataResponseType, { data: any; selectedValue: number; page: number }>({
            query: params => ({
                url: `${MODULE_URL}/${params.data.parent_status ?? "all_tab"}`,
                params: {
                    search_key: params.data.search_key ?? "",
                    child_status_ids: JSON.stringify(params.data.child_status_ids ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    country_ids: JSON.stringify(params.data.country_ids ?? []),
                    zone_ids: JSON.stringify(params.data.zone_ids ?? []),
                    region_ids: JSON.stringify(params.data.region_ids ?? []),
                    state_ids: JSON.stringify(params.data.state_ids ?? []),
                    city_ids: JSON.stringify(params.data.city_ids ?? []),
                    site_ids: JSON.stringify(params.data.site_ids ?? []),
                    raised_on_from: params.data.raised_on_from ?? "",
                    raised_on_to: params.data.raised_on_to ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    payment_status: JSON.stringify(params.data.payment_status ?? []),
                    due_date_start: params.data.due_date_start ?? "",
                    due_date_end: params.data.due_date_end ?? "",
                    tag_ids: JSON.stringify(params.data.tag_ids ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    on_hold: params.data.on_hold ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    on_hold_date: params.data.on_hold_date ?? "",
                    product_ids: JSON.stringify(params.data.product_ids ?? []),
                    per_page: params?.selectedValue,
                    page: params?.page,
                    account_id: JSON.stringify(params.data.account_id ?? []),
                    parent_account_id: JSON.stringify(params.data.parent_account_id ?? []),
                    ecom_order_id: params.data.ecom_order_id ? JSON.stringify([params.data.ecom_order_id]) : JSON.stringify([]),
                },
                useV3: true,
            }),
            providesTags: ["Finance"],
        }),
        downloadFinance: build.query<IFile | { message: string; success: boolean }, { data: any }>({
            query: params => ({
                url: `${MODULE_URL}/download`,
                params: {
                    parent_status: params.data.parent_status ?? "all_tab",
                    search_key: params.data.search_key ?? "",
                    child_status_ids: JSON.stringify(params.data.child_status_ids ?? []),
                    assigned_to: params.data.assigned_to?.id ?? "",
                    country_ids: JSON.stringify(params.data.country_ids ?? []),
                    zone_ids: JSON.stringify(params.data.zone_ids ?? []),
                    region_ids: JSON.stringify(params.data.region_ids ?? []),
                    state_ids: JSON.stringify(params.data.state_ids ?? []),
                    city_ids: JSON.stringify(params.data.city_ids ?? []),
                    site_ids: JSON.stringify(params.data.site_ids ?? []),
                    raised_on_from: params.data.raised_on_from ?? "",
                    raised_on_to: params.data.raised_on_to ?? "",
                    age: JSON.stringify(params.data.age ?? []),
                    age_op: params.data.age_op ?? "",
                    payment_status: JSON.stringify(params.data.payment_status ?? []),
                    due_date_start: params.data.due_date_start ?? "",
                    due_date_end: params.data.due_date_end ?? "",
                    tag_ids: JSON.stringify(params.data.tag_ids ?? []),
                    category_ids: JSON.stringify(
                        (params.data.category_ids ?? []).map((i: any) => (typeof i === "object" ? i.id : i))
                    ),
                    on_hold: params.data.on_hold ?? "",
                    on_hold_reason_ids: JSON.stringify(params.data.on_hold_reason_ids ?? []),
                    on_hold_date: params.data.on_hold_date ?? "",
                    product_ids: JSON.stringify(params.data.product_ids ?? []),
                },
                useV3: true,
            }),
            onQueryStarted: async (_arg, { queryFulfilled }) => {
                await handleSnackbar(queryFulfilled, d => d?.message)
            },
            keepUnusedDataFor: 0,
        }),
        getFinaneOverviewDetails: build.query<
            IFinanceOverviewResponse,
            { ticket_id: string | number; account_type?: string }
        >({
            query: ({ ticket_id, account_type }) => ({
                url: `${MODULE_URL}/${ticket_id}/overview`,
                params: {
                    ...(account_type ? { account_type } : {}),
                },
                useV3: true,
            }),
            transformResponse: (response: any) => response?.message ?? {},
            providesTags: ["Finance"],
        }),
        getFinanceDocHub: build.query<IDocHubDocument[], FinanceDocHubFilters>({
            query: params => ({
                url: `${MODULE_URL}/${params.ticket_id}/doc_hub`,
                params,
                useV3: true,
            }),
            transformResponse: (response: { message: { data: IDocHubDocument[] } }) => {
                return response?.message?.data || []
            },
            providesTags: ["Finance"],
        }),
        getFinanceMaterialDetail: build.query<IProjectMaterialDetailsResponse, string>({
            query: ticket_id => ({
                url: `${MODULE_URL}/${ticket_id}/material_details`,
                useV3: true,
            }),
            transformResponse: (response: { message: IProjectMaterialDetailsResponse; success: boolean }) => {
                return response?.message
            },
            providesTags: ["Finance"],
        }),
        getPODDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/get_pd_details`,
                params: {
                    ticket_id,
                },
            }),
        }),
        getFinancePurchaseOrders: build.query<PurchaseOrderDetailsResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/po?po_type=receivable`,
                useV3: true,
            }),
            transformResponse: (response: any): PurchaseOrderDetailsResponse => response?.message ?? [],
        }),
        getFinanceInvoiceList: build.query<IFinanceInvoiceListItem[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/invoice-list`,
                useV3: true,
            }),
            transformResponse: (response: any): IFinanceInvoiceListItem[] =>
                response?.data ?? response?.message ?? (Array.isArray(response) ? response : []),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        getFinanceDcList: build.query<IFinanceDcListItem[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/dc-list`,
                useV3: true,
            }),
            transformResponse: (response: any): IFinanceDcListItem[] =>
                response?.message ?? (Array.isArray(response) ? response : []),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        getTransferOrderList: build.query<ITransferOrderListItem[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/transfer-order-list`,
                useV3: true,
            }),
            transformResponse: (response: any): ITransferOrderListItem[] =>
                response?.data ?? response?.message ?? (Array.isArray(response) ? response : []),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        getFinanceItems: build.query<IFinanceItemsResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/items`,
                useV3: true,
            }),
            transformResponse: (response: any): IFinanceItemsResponse =>
                response?.items ? response : (response?.message ?? { group_by: null, count: 0, items: [] }),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        getFinanceItemsGrouped: build.query<IFinanceItemsGroupedResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/items`,
                params: { group_by: "product_id" },
                useV3: true,
            }),
            transformResponse: (response: any): IFinanceItemsGroupedResponse =>
                response?.items ? response : (response?.message ?? { group_by: "product_id", count: 0, items: [] }),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        createSentOnInvoice: build.mutation<ICreateSentOnInvoiceResponse, { ticket_id: string; body: ICreateSentOnInvoicePayload }>({
            query: ({ ticket_id, body }) => ({
                url: `${MODULE_URL}/${ticket_id}/sent-on/invoice`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        linkSentOnItemParents: build.mutation<ILinkItemParentsResponse, ILinkItemParentsPayload>({
            query: body => ({
                url: `${MODULE_URL}/sent-on/item-parents`,
                method: "PUT",
                useV3: true,
                body,
            }),
            invalidatesTags: ["Finance"],
        }),
        pushZohoInvoice: build.mutation<IZohoInvoicePushResponse, IZohoInvoicePushPayload>({
            query: body => ({
                url: `${MODULE_URL}/zoho/invoice/create`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        syncZohoInvoiceStatus: build.mutation<IZohoInvoiceStatusResponse, IZohoInvoiceStatusPayload>({
            query: body => ({
                url: `${MODULE_URL}/zoho/invoice/status`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        createTransferOrder: build.mutation<
            ICreateTransferOrderResponse,
            { ticket_id: string; body: ICreateTransferOrderPayload }
        >({
            query: ({ ticket_id, body }) => ({
                url: `${MODULE_URL}/zoho/transfer-order/create`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        pushTransferOrder: build.mutation<any, { ticket_id: string; body: ITransferOrderActionPayload }>({
            query: ({ body }) => ({
                url: `${MODULE_URL}/zoho/transfer-order/push`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        initiateTransferOrder: build.mutation<any, { ticket_id: string; body: ITransferOrderActionPayload }>({
            query: ({ body }) => ({
                url: `${MODULE_URL}/zoho/transfer-order/initiate`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        markTransferOrderTransferred: build.mutation<any, { ticket_id: string; body: ITransferOrderActionPayload }>({
            query: ({ body }) => ({
                url: `${MODULE_URL}/zoho/transfer-order/mark-transferred`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        // Invalidating "Accounts" refetches list_accounts, which carries the written-back
        // zoho_customer_id / billing_addresses[].zoho_id.
        createZohoCustomer: build.mutation<ICreateZohoCustomerResponse, ICreateZohoCustomerPayload>({
            query: body => ({
                url: `${MODULE_URL}/zoho/customer/create`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: ["Accounts"],
        }),
        addZohoCustomerAddress: build.mutation<IAddZohoCustomerAddressResponse, IZohoCustomerAddressPayload>({
            query: body => ({
                url: `${MODULE_URL}/zoho/customer/address`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: ["Accounts"],
        }),
        markZohoAddressPrimary: build.mutation<IMarkZohoAddressPrimaryResponse, IZohoCustomerAddressPayload>({
            query: body => ({
                url: `${MODULE_URL}/zoho/customer/address/mark-primary`,
                method: "POST",
                useV3: true,
                body,
            }),
            invalidatesTags: ["Accounts"],
        }),
        getTicketDetails: build.query<any, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/ticket_details`,
                useV3: true,
            }),
            transformResponse: (response: any) => response.message,
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getOtherDetails: build.query<IResponse<any>, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/other_details`,
                params: {
                    ticket_id,
                },
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentTermsData: build.query<any, { ticket_id: string; ticket_type?: string }>({
            query: params => ({
                url: `${MODULE_URL}/get_payment_terms`,
                params: {
                    ticket_id: params.ticket_id,
                    ticket_type: params?.ticket_type || "",
                },
                useV3: true,
            }),
        }),
        getChargerFlag: build.query<any, { ticket_id: string; project_id: string }>({
            query: params => ({
                url: `${MODULE_URL}/approval_charger_modal`,
                params: {
                    ticket_id: params.ticket_id,
                    project_id: params?.project_id || "",
                },
            }),
        }),
        getSkipStatuses: build.query<IResponse<any>, GetSkipStatusesParams>({
            query: ({ search_key, ticket_id }) => ({
                url: `${MODULE_URL}/statuses`,
                params: {
                    search_key: search_key ?? "",
                },
                useV3: true,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg?.ticket_id }],
        }),
        getTicketHistory: build.query<HistoryResponse[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/history`,
                useV3: true,
            }),
            transformResponse: (response: any): HistoryResponse[] => response?.message ?? [],
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentConditions: build.query<{ id: number; name: string }[], { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/conditions`,
                params: {
                    ticket_id,
                },
                useV3: true,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getPaymentTime: build.query<HistoryResponse, { ticket_id: string }>({
            query: () => ({
                url: `${MODULE_URL}/time_uom`,
            }),
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        // for getting button dropdown options
        getFinanceButtonDropdown: build.query({
            query: (params: { ticket_id: string }) => ({
                url: `${MODULE_URL}/button`,
                params: params,
                useV3: true,
            }),
            transformResponse: (response: IResponse<{ message: StatusButton[] }>) => response.data.message,
            providesTags: (result, error, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        getChargerTemplate: build.mutation<any, { ticket_id: string; charger_details: any }>({
            query: body => ({
                url: `${MODULE_URL}/charger_operation`,
                method: "POST",
                useV3: true,
                body: body,
                responseHandler: (response: Response) => response.blob(),
                cache: "no-cache",
            }),
            async onQueryStarted(arg, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const url = window.URL.createObjectURL(data)
                    const a = document.createElement("a")
                    a.href = url
                    a.download = `charger_template_${arg.ticket_id}.xlsx`
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                } catch (error) {
                    console.error("Download failed:", error)
                }
            },
        }),
        getTaxInvoices: build.query<any, { id?: string; ticket_id?: string }>({
            query: params => ({
                url: `${MODULE_URL}/tax_invoice`,
                params,
                useV3: true,
            }),
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        getPaymentTermsV3: build.query<PaymentTermsResponse, { ticket_id: string }>({
            query: ({ ticket_id }) => ({
                url: `${MODULE_URL}/${ticket_id}/payment_terms`,
                useV3: true,
            }),
            transformResponse: (response: any): PaymentTermsResponse => response?.message ?? [],
            providesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }, "Finance"],
        }),
        uploadBulkCharger: build.mutation<{ message: string }, { ticket_id: string; payload: FormData }>({
            query: body => ({
                url: `${MODULE_URL}/charger_operation`,
                method: "PUT",
                useV3: true,
                body: body.payload,
            }),
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg.ticket_id }],
        }),
        // for changing ticket status
        editFinanceTicketStatus: build.mutation<IResponse<unknown>, { ticket_id: string; body: FormData }>({
            query: ({ ticket_id, body }) => ({
                url: `${MODULE_URL}/${ticket_id}/update_status`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.ticket_id },
                "Finance",
                "Logistics",
                "Installation",
            ],
        }),
        editSkipStatus: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/skip_to_status`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        editTaxInvoice: build.mutation<{ msg: string }, FormData>({
            query: body => ({
                url: `${MODULE_URL}/tax_invoice`,
                method: "PUT",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        addTicketDetails: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/ticket_details`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        addPaymentTerm: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/payment_term`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),
        addTransaction: build.mutation<IResponse<{ message: string }>, { data: FormData; module?: string }>({
            query: body => ({
                url: `${MODULE_URL}/transaction`,
                method: "POST",
                useV3: true,
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const urlParams = new URLSearchParams(window.location.search)
                    const ticketId = urlParams.get("ticket_id")
                    if (arg?.module === "demolitions") {
                        dispatch(getDemolitionTicketDetails(ticketId))
                    }
                    enqueueSnackbar(data.data.message, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong", { variant: "error" })
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: "Finance", id: arg.data.get("ticket_id") as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),
        editTransaction: build.mutation<
            { msg: string },
            { data: FormData; view?: "table" | "details"; module?: string }
        >({
            query: body => ({
                url: `${MODULE_URL}/transaction`,
                method: "PUT",
                body: body.data,
                useV3: true,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.msg, { variant: "success" })

                    const urlParams = new URLSearchParams(window.location.search)
                    const ticketId = urlParams.get("ticket_id")
                    if (arg?.module === "demolitions") {
                        dispatch(getDemolitionTicketDetails(ticketId))
                    }
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong", { variant: "error" })
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: "Finance", id: arg.data.get("ticket_id") as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),

        addTaxInvoice: build.mutation<{ msg: string }, { data: FormData; invalidateCache?: boolean }>({
            query: body => ({
                url: `${MODULE_URL}/tax_invoice`,
                method: "POST",
                useV3: true,
                body: body.data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) =>
                arg.invalidateCache === false
                    ? []
                    : [{ type: "Finance", id: arg.data.get("ticket_id") as string }, "Finance"],
        }),
        VerifyTransaction: build.mutation<IResponse<unknown>, FormData>({
            query: body => ({
                url: `${MODULE_URL}/verify_transaction`,
                method: "POST",
                useV3: true,
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    const msg =
                        typeof data?.data === "string"
                            ? data.data
                            : ((data?.data as any)?.message ?? "Transaction verified")
                    dispatch(setAlertFunc({ type: "success", msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg.get("ticket_id") as string },
                "Finance",
            ],
        }),
        deleteTransaction: build.mutation<IResponse<any>, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/transaction?ticket_id=${data?.ticket_id}&transaction_id=${data?.transaction_id}`,
                useV3: true,
                method: "DELETE",
            }),
            async onQueryStarted(_, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.data, { variant: "success" })
                } catch (error: any) {
                    enqueueSnackbar(error.message || "Something went wrong!", { variant: "error" })
                }
            },
            invalidatesTags: (_, __, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
                "Projects",
                "Survey",
            ],
        }),
        deletePaymentTerm: build.mutation<IResponse<any>, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/payment_term?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&payment_term_id=${data?.payment_term_id}`,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        deleteTaxInvoice: build.mutation<{ msg: string }, { data: any }>({
            query: ({ data }) => ({
                url: `${MODULE_URL}/tax_invoice?ticket_id=${data?.ticket_id}&user_id=${data?.user_id}&id=${data?.id}&sent_on_type=${data?.sent_on_type ?? "invoice"}`,
                useV3: true,
                method: "DELETE",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.msg }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error?.message }))
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: "Finance", id: arg?.data?.ticket_id as string },
                "Finance",
            ],
        }),
        addPaymentTermsInBulk: build.mutation<IResponse<any>, FormData>({
            query: data => ({
                url: `${MODULE_URL}/add_payment_terms_in_bulk`,
                method: "POST",
                body: data,
                useV3: true,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.data }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: (_, __, arg) => [{ type: "Finance", id: arg?.get("ticket_id") as string }, "Finance"],
        }),
        extractTaxInvoice: build.mutation<IExtractTaxInvoiceRes, FormData>({
            query: data => ({
                url: `${MODULE_URL}/extract-tax-invoice`,
                method: "POST",
                body: data,
                useV3: true,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    enqueueSnackbar(error.error?.data?.error, { variant: "error" })
                }
            },
        }),
        saveTaxInvoiceData: build.mutation<{ msg: string }, FormData>({
            query: data => ({
                url: `${MODULE_URL}/tax-invoice`,
                method: "POST",
                body: data,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.error?.data?.error }))
                }
            },
        }),
        getFinanceLinkedTickets: build.query<any, { ticketId: number | string }>({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/tickets`,
                useV3: true,
            }),
            providesTags: ["Finance", "Logistics", "Projects"],
        }),
        unrejectFinanceTicket: build.mutation<{ message: string }, { ticketId: string | number }>({
            query: ({ ticketId }) => ({
                url: `${MODULE_URL}/${ticketId}/unreject`,
                useV3: true,
                method: "PUT",
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    enqueueSnackbar(data?.message)
                } catch (error: any) {
                    enqueueSnackbar(error.error?.data?.error, { variant: "error" })
                    dispatch(setAlertFunc({ type: "error", msg: error.error?.data?.error }))
                }
            },
            invalidatesTags: ["Projects", "Finance", "Logistics"],
        }),
    }),
})
export const {
    useLazyGetFinanceTabsQuery,
    useLazyGetFinanceDataQuery,
    useGetTicketDetailsQuery,
    useGetTicketHistoryQuery,
    useGetFinanceDocHubQuery,
    useGetFinanceMaterialDetailQuery,
    useGetFinanceButtonDropdownQuery,
    useGetPaymentTermsV3Query,
    useLazyGetPaymentTermsV3Query,
    useLazyGetPaymentTermsDataQuery,
    useLazyDownloadFinanceQuery,
    useLazyGetPODDetailsQuery,
    useGetSkipStatusesQuery,
    useGetChargerFlagQuery,
    useGetFinaneOverviewDetailsQuery,
    useGetOtherDetailsQuery,
    useGetPaymentConditionsQuery,
    useGetFinancePurchaseOrdersQuery,
    useLazyGetFinancePurchaseOrdersQuery,
    useGetFinanceInvoiceListQuery,
    useLazyGetFinanceInvoiceListQuery,
    useGetFinanceDcListQuery,
    useGetTransferOrderListQuery,
    useGetFinanceItemsQuery,
    useGetFinanceItemsGroupedQuery,
    useCreateSentOnInvoiceMutation,
    useLinkSentOnItemParentsMutation,
    usePushZohoInvoiceMutation,
    useSyncZohoInvoiceStatusMutation,
    useCreateTransferOrderMutation,
    usePushTransferOrderMutation,
    useInitiateTransferOrderMutation,
    useMarkTransferOrderTransferredMutation,
    useCreateZohoCustomerMutation,
    useAddZohoCustomerAddressMutation,
    useMarkZohoAddressPrimaryMutation,
    useGetPaymentTimeQuery,
    useGetTaxInvoicesQuery,
    useGetChargerTemplateMutation,
    useEditFinanceTicketStatusMutation,
    useEditSkipStatusMutation,
    useEditTaxInvoiceMutation,
    useVerifyTransactionMutation,
    useAddPaymentTermMutation,
    useAddTaxInvoiceMutation,
    useAddTicketDetailsMutation,
    useEditTransactionMutation,
    useAddTransactionMutation,
    useDeletePaymentTermMutation,
    useDeleteTaxInvoiceMutation,
    useDeleteTransactionMutation,
    useUploadBulkChargerMutation,
    useAddPaymentTermsInBulkMutation,
    useExtractTaxInvoiceMutation,
    useSaveTaxInvoiceDataMutation,
    useGetFinanceLinkedTicketsQuery,
    useUnrejectFinanceTicketMutation,
} = financeService

export { financeService }
